<script setup lang="ts">
import { ref } from 'vue';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import type { Button } from '@pkg/sanity-utils/types/button';
import { ButtonGroup } from '../ButtonGroup';
import CmsIcon from '../Tiles/CmsIcon.vue';
import TitleBlock from '../Title/TitleBlock.vue';
import type { Statistic } from './types';

interface Props {
  pretitle?: string;
  title?: string;
  button?: Button,
  items: Array<Statistic>,
  source?: string;
}

defineProps<Props>();

const lazy = ref(false);

if (import.meta.client) {
  setInterval(() => {
    lazy.value = true;
  }, 500);
}
</script>

<template>
  <div class="c-stats">
    <TitleBlock v-if="title || pretitle" variant="center" :title="title" :pre-title="pretitle" />

    <div class="c-stats__items">
      <div v-for="item in items" :key="`${item.icon}-${item.context}`" class="c-stats__item">
        <CmsIcon v-if="lazy" :name="item.icon" class="c-stats__item-icon" />

        <h4 class="c-stats__item-stat">
          <span v-if="item.prefix" class="text-h5">
            {{ item.prefix.toUpperCase() }}
          </span>
          <span v-if="item.stat" class="text-h2">
            {{ item.stat.toUpperCase() }}
          </span>
          <span v-if="item.suffix" class="text-h5">
            {{ item.suffix.toUpperCase() }}
          </span>
          <span v-if="item.context" class="text-h4 uppercase">
            {{ item.context }}
          </span>
        </h4>

        <PortableEditor :content="item.content" class="c-stats__item-content" />

        <p class="c-stats__item-source text-body--xsmall">
          {{ item.source }}
        </p>
      </div>
    </div>

    <div v-if="button" class="c-stats__action">
      <ButtonGroup :buttons="[button]" />
    </div>
  </div>
</template>

<style scoped>
.c-stats {
  --statistics-grid-gap: theme('spacing.32');

  @screen tablet-wide {
    --statistics-grid-gap: theme('spacing.24');
  }

  display: flex;
  flex-direction: column;
  gap: theme('spacing.32')
}

.c-stats__items {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--statistics-grid-gap);

  @screen tablet-wide {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }
}

.c-stats__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  gap: theme('spacing.4');

  @screen tablet {
    width: calc(50% - (var(--statistics-grid-gap) / 2));
  }
  @screen tablet-wide {
    gap: theme('spacing.8');
  }
  @screen desk {
    width: calc(33% - (var(--statistics-grid-gap) / 2));
  }
}

.c-stats__item-icon {
  color: var(--statistics-icon-color);
  font-size: 3em;

  @screen tablet-wide {
    font-size: 4em;
  }
}

.c-stats__item-stat {
  color: var(--theme-heading-color);
  display: flex;
  flex-direction: column;
}

/*
 * Size lives on the element, not only on the global `.text-h*` utility.
 * Business does not give `.text-h2` a font-size, so the figure was falling
 * back to the body size.
 */
.c-stats__item-stat .text-h2 {
  font-family: theme('fontFamily.heading');
  font-weight: theme('fontWeight.black');
  letter-spacing: theme('letterSpacing.normal');
  line-height: theme('lineHeight.tight');
  font-size: theme('fontSize.32');

  @screen tablet-wide {
    font-size: theme('fontSize.44');
  }
}

.c-stats__item-stat .text-h5 {
  font-family: theme('fontFamily.heading');
  font-weight: theme('fontWeight.black');
  letter-spacing: theme('letterSpacing.normal');
  line-height: theme('lineHeight.snug');
  font-size: theme('fontSize.16');

  @screen tablet-wide {
    font-size: theme('fontSize.20');
  }
}

.c-stats__item-stat .text-h4 {
  font-family: theme('fontFamily.heading');
  font-weight: theme('fontWeight.black');
  letter-spacing: theme('letterSpacing.normal');
  line-height: theme('lineHeight.snug');
  font-size: theme('fontSize.20');

  @screen tablet-wide {
    font-size: theme('fontSize.24');
  }
}

.c-stats__item-content {
  color: var(--theme-text-color);
}

.c-stats__action {
  display: flex;
  justify-content: center;
}
</style>
